import type { Invoice, InvoiceItem, InvoiceSettingsRow, Payment } from "@/types";
import { printFullHtml, type PrintJobOptions } from "@/lib/printing";
import { buildThermalTicketHtml, type ThermalPaper, type ThermalTicketData } from "@/lib/thermalInvoice";

export type InvoiceFormat = "pos58" | "pos80" | "a4";

export interface InvoicePrintData {
  invoice: Invoice;
  items: InvoiceItem[];
  payments?: Payment[];
  company: {
    name: string;
    rnc?: string | null;
    address?: string | null;
    phone?: string | null;
    email?: string | null;
    whatsapp?: string | null;
    logoUrl?: string | null;
  };
  branch?: {
    name?: string | null;
    address?: string | null;
    phone?: string | null;
    rnc?: string | null;
  } | null;
  customer?: {
    name?: string | null;
    document?: string | null;
    phone?: string | null;
    address?: string | null;
  } | null;
  cashier?: string | null;
  posSettings?: InvoiceSettingsRow | null;
  a4Settings?: InvoiceSettingsRow | null;
  /** Datos de facturación electrónica. Solo se imprimen si `enabled` es true. */
  fiscal?: {
    enabled?: boolean;
    receiptType?: string | null;
    title?: string | null;
    ncf?: string | null;
    ncfLabel?: string | null;
    ncfExpiry?: string | null;
    securityCode?: string | null;
    qrDataUrl?: string | null;
    signedAt?: string | null;
  } | null;
}

export const STATUS_LABELS: Record<string, string> = {
  paid: "Pagada",
  pending: "Pendiente",
  void: "Anulada",
  refunded: "Reembolsada",
  partially_refunded: "Parcialmente reembolsada",
};

export const STATUS_STYLES: Record<string, string> = {
  paid: "bg-accent-100 text-accent-800",
  pending: "bg-secondary-100 text-secondary-900",
  void: "bg-background-200 text-foreground-600",
  refunded: "bg-primary-100 text-primary-700",
  partially_refunded: "bg-primary-100 text-primary-700",
};

function escapeHtml(value: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function money(value: number | null | undefined): string {
  return `RD$ ${Number(value ?? 0).toLocaleString("es-DO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function qty(value: number | null | undefined): string {
  return Number(value ?? 0).toLocaleString("es-DO", { maximumFractionDigits: 3 });
}

function dateLabel(value?: string | null): string {
  if (!value) return new Date().toLocaleString("es-DO");
  return new Date(value).toLocaleString("es-DO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Separa fecha y hora del sello de la factura para el ticket térmico. */
function splitDateTime(value?: string | null): { date: string; time: string } {
  const dt = value ? new Date(value) : new Date();
  if (Number.isNaN(dt.getTime())) {
    const now = new Date();
    return {
      date: now.toLocaleDateString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric" }),
      time: now.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" }),
    };
  }
  return {
    date: dt.toLocaleDateString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric" }),
    time: dt.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" }),
  };
}

/**
 * Etiqueta de forma de pago. Si hay varios métodos distintos se resume como "Mixto",
 * igual que una factura comercial real.
 */
function paymentLabel(payments: Payment[] | undefined): string | null {
  if (!payments || payments.length === 0) return null;
  const methods = Array.from(
    new Set(payments.map((p) => (p.method ?? "").trim()).filter((m) => m !== "")),
  );
  if (methods.length === 0) return null;
  return methods.length === 1 ? methods[0] : "Mixto";
}

interface TicketMeta {
  contactLines: string[];
}

function buildContactLines(
  data: InvoicePrintData,
  settings?: InvoiceSettingsRow | null,
  includeFiscalId = true,
): TicketMeta {
  const contactLines: string[] = [];
  const { company, branch } = data;
  if (includeFiscalId && settings?.show_rnc !== false && company.rnc) {
    contactLines.push(`RNC: ${escapeHtml(company.rnc)}`);
  }
  if (settings?.show_address !== false) {
    const addr = branch?.address || company.address;
    if (addr) contactLines.push(escapeHtml(addr));
  }
  if (settings?.show_phone !== false) {
    const tel = branch?.phone || company.phone;
    if (tel) contactLines.push(`Tel: ${escapeHtml(tel)}`);
  }
  return { contactLines };
}

/** Mapea los datos de la factura al modelo de la plantilla térmica. */
function mapThermalData(data: InvoicePrintData, show?: InvoiceSettingsRow | null): ThermalTicketData {
  const { invoice, items, company, branch, customer, cashier, fiscal } = data;
  const { date, time } = splitDateTime(invoice.created_at);

  // La facturación fiscal solo se muestra si está activa Y el NCF/e-CF está habilitado.
  const fiscalEnabled = Boolean(fiscal?.enabled) && show?.show_ncf !== false;
  // Factura normal (sin comprobante fiscal): el título siempre es "Factura".
  const documentTitle = fiscalEnabled ? fiscal?.title || "Factura de Crédito Fiscal" : "Factura";

  const unitCount = items.reduce((sum, item) => sum + Number(item.quantity ?? 0), 0);

  return {
    company: {
      name: branch?.name || company.name,
      rnc: branch?.rnc || company.rnc,
      address: branch?.address || company.address,
      phone: branch?.phone || company.phone,
      whatsapp: company.whatsapp,
      email: company.email,
      logoUrl: company.logoUrl,
      headerText: show?.header_text ?? null,
    },
    invoice: {
      number: invoice.number,
      documentTitle,
      dateLabel: date,
      timeLabel: time,
      subtotal: Number(invoice.subtotal ?? 0),
      discountTotal: Number(invoice.discount_total ?? 0),
      taxTotal: Number(invoice.tax_total ?? 0),
      total: Number(invoice.total ?? 0),
      unitCount,
    },
    items: items.map((item) => ({
      description: item.description ?? "",
      quantity: Number(item.quantity ?? 0),
      unitPrice: Number(item.unit_price ?? 0),
      discount: item.discount ?? null,
      lineTotal: Number(item.line_total ?? 0),
    })),
    customer: {
      name: customer?.name ?? null,
      document: customer?.document ?? null,
      phone: customer?.phone ?? null,
      address: customer?.address ?? null,
      paymentLabel: paymentLabel(data.payments),
    },
    cashier,
    visibility: {
      showLogo: show?.show_logo !== false,
      // El RNC del negocio es dato fiscal: solo se imprime con comprobante activo.
      showRnc: fiscalEnabled && show?.show_rnc !== false,
      showAddress: show?.show_address !== false,
      showPhone: show?.show_phone !== false,
      showSocial: show?.show_social !== false,
      showCustomer: show?.show_customer !== false,
      showCashier: show?.show_cashier !== false,
      showDiscount: show?.show_discount !== false,
      showTax: show?.show_tax !== false,
      showNcf: show?.show_ncf !== false,
      showThanks: show?.show_thanks !== false,
      showReturnMessage: show?.show_return_message !== false,
      thanksText: show?.thanks_text ?? null,
      returnText: show?.return_text ?? null,
      footerText: show?.footer_text ?? null,
    },
    fiscal: fiscal
      ? {
          enabled: fiscalEnabled,
          title: fiscal.title ?? null,
          ncfLabel: fiscal.ncfLabel ?? "e-NCF",
          ncf: fiscal.ncf ?? invoice.ncf ?? null,
          ncfExpiry: fiscal.ncfExpiry ?? null,
          securityCode: fiscal.securityCode ?? null,
          qrDataUrl: fiscal.qrDataUrl ?? null,
          signedAt: fiscal.signedAt ?? null,
        }
      : { enabled: false },
  };
}

/** Thermal ticket (POS 58mm / 80mm) built with the professional thermal template. */
export function buildPosTicketHtml(data: InvoicePrintData, widthMm: number): string {
  const paper: ThermalPaper = widthMm <= 58 ? "pos58" : "pos80";
  return buildThermalTicketHtml(mapThermalData(data, data.posSettings), paper);
}

/** A4 / PDF invoice. Returns a complete HTML document. */
export function buildA4InvoiceHtml(data: InvoicePrintData): string {
  const { invoice, items, company, branch, customer, cashier, a4Settings, payments } = data;
  const show = a4Settings;

  // Se oculta TODO lo fiscal cuando el comprobante está desactivado o cuando el
  // usuario apagó "Mostrar NCF / e-CF" para el formato A4.
  const fiscalEnabled = Boolean(data.fiscal?.enabled) && show?.show_ncf !== false;
  const { contactLines } = buildContactLines(data, show, fiscalEnabled);

  const rows = items
    .map(
      (item) => `
        <tr>
          <td>${escapeHtml(item.description)}</td>
          <td class="center">${qty(item.quantity)}</td>
          <td class="right">${money(item.unit_price)}</td>
          <td class="right">${Number(item.discount ?? 0) > 0 ? `-${money(item.discount)}` : "-"}</td>
          <td class="right">${money(item.line_total)}</td>
        </tr>`,
    )
    .join("");

  const paymentRows = (payments ?? [])
    .map(
      (p) =>
        `<li>${escapeHtml(p.method ?? "")}${p.reference ? ` · Ref: ${escapeHtml(p.reference)}` : ""} — ${money(
          p.amount,
        )}</li>`,
    )
    .join("");

  const logo =
    show?.show_logo !== false && company.logoUrl
      ? `<img src="${escapeHtml(company.logoUrl)}" alt="Logo" style="max-height:${show?.logo_size ?? 120}px;" />`
      : "";

  return `<!doctype html><html><head><meta charset="utf-8" /><title>Factura ${escapeHtml(invoice.number ?? "")}</title><style>
    @page { size: A4; margin: 14mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
    body { font-family: "Segoe UI", Arial, sans-serif; margin: 0; color: #1a1a1a; font-size: 12px; }
    .top { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #b91c1c; padding-bottom: 12px; }
    .company-name { font-size: 20px; font-weight: 800; color: #b91c1c; }
    .muted { color: #555; font-size: 11px; line-height: 1.5; }
    .doc { text-align: right; min-width: 200px; }
    .doc-title { font-size: 18px; font-weight: 800; letter-spacing: 2px; }
    .box { margin-top: 18px; display: flex; justify-content: space-between; gap: 24px; }
    .box h4 { margin: 0 0 4px; font-size: 11px; text-transform: uppercase; color: #777; letter-spacing: 1px; }
    table { width: 100%; border-collapse: collapse; margin-top: 18px; table-layout: fixed; }
    thead { display: table-header-group; }
    tbody tr { page-break-inside: avoid; break-inside: avoid; }
    thead th { background: #f3f4f6; text-align: left; padding: 8px; font-size: 11px; text-transform: uppercase; color: #444; overflow-wrap: anywhere; }
    tbody td { padding: 8px; border-bottom: 1px solid #e5e7eb; overflow-wrap: anywhere; vertical-align: top; }
    .right { text-align: right; }
    td.right { white-space: nowrap; }
    .center { text-align: center; }
    .totals { margin-top: 16px; margin-left: auto; width: 260px; }
    .totals .row { display: flex; justify-content: space-between; padding: 4px 0; }
    .totals .grand { border-top: 2px solid #b91c1c; margin-top: 6px; padding-top: 8px; font-size: 15px; font-weight: 800; color: #b91c1c; }
    .footer { margin-top: 28px; border-top: 1px solid #e5e7eb; padding-top: 12px; }
    .thanks { margin-top: 18px; text-align: center; font-weight: 600; }
    .fiscal { margin-top: 16px; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px 12px; font-size: 11px; }
  </style></head><body>
    <div class="top">
      <div>
        ${logo}
        <div class="company-name">${escapeHtml(branch?.name || company.name)}</div>
        <div class="muted">
          ${show?.header_text ? `${escapeHtml(show.header_text)}<br/>` : ""}
          ${contactLines.join("<br/>")}
        </div>
      </div>
      <div class="doc">
        <div class="doc-title">FACTURA</div>
        <div class="muted">
          No. <strong>${escapeHtml(invoice.number ?? "-")}</strong><br/>
          Fecha: ${escapeHtml(dateLabel(invoice.created_at))}<br/>
          ${show?.show_cashier !== false && cashier ? `Cajero: ${escapeHtml(cashier)}` : ""}
        </div>
      </div>
    </div>

    ${
      fiscalEnabled
        ? `<div class="fiscal">
            <strong>${escapeHtml(data.fiscal?.title || "Factura de Crédito Fiscal Electrónica")}</strong><br/>
            ${data.fiscal?.ncf || invoice.ncf ? `${escapeHtml(data.fiscal?.ncfLabel || "e-NCF")}: ${escapeHtml(data.fiscal?.ncf || invoice.ncf || "")}<br/>` : ""}
            ${data.fiscal?.ncfExpiry ? `Vence: ${escapeHtml(data.fiscal.ncfExpiry)}<br/>` : ""}
            ${data.fiscal?.securityCode ? `Código de Seguridad: ${escapeHtml(data.fiscal.securityCode)}<br/>` : ""}
            ${data.fiscal?.signedAt ? `Firma Digital: ${escapeHtml(data.fiscal.signedAt)}` : ""}
          </div>`
        : ""
    }

    ${
      show?.show_customer !== false
        ? `<div class="box">
            <div>
              <h4>Cliente</h4>
              <div>${escapeHtml(customer?.name || "Consumidor final")}</div>
              <div class="muted">${customer?.document ? `Doc: ${escapeHtml(customer.document)}` : ""} ${
                customer?.phone ? `<br/>Tel: ${escapeHtml(customer.phone)}` : ""
              }</div>
            </div>
            <div>
              <h4>Sucursal</h4>
              <div>${escapeHtml(branch?.name || "")}</div>
              <div class="muted">${escapeHtml(branch?.address || "")}</div>
            </div>
          </div>`
        : ""
    }

    <table>
      <colgroup>
        <col style="width: 44%" />
        <col style="width: 10%" />
        <col style="width: 17%" />
        <col style="width: 12%" />
        <col style="width: 17%" />
      </colgroup>
      <thead>
        <tr><th>Descripción</th><th class="center">Cant.</th><th class="right">Precio</th><th class="right">Desc.</th><th class="right">Importe</th></tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>

    <div class="totals">
      <div class="row"><span>Subtotal</span><span>${money(invoice.subtotal)}</span></div>
      ${show?.show_discount !== false ? `<div class="row"><span>Descuento</span><span>-${money(invoice.discount_total)}</span></div>` : ""}
      ${show?.show_tax !== false ? `<div class="row"><span>Impuestos</span><span>${money(invoice.tax_total)}</span></div>` : ""}
      <div class="row grand"><span>TOTAL</span><span>${money(invoice.total)}</span></div>
    </div>

    ${
      paymentRows
        ? `<div class="box"><div><h4>Pagos</h4><ul class="muted" style="padding-left:16px;margin:0;">${paymentRows}</ul></div></div>`
        : ""
    }

    <div class="footer muted">
      ${show?.footer_text ? `${escapeHtml(show.footer_text)}<br/>` : ""}
      ${show?.show_return_message !== false && show?.return_text ? `${escapeHtml(show.return_text)}` : ""}
    </div>
    ${show?.show_thanks !== false ? `<div class="thanks">${escapeHtml(show?.thanks_text || "¡Gracias por su compra!")}</div>` : ""}
  </body></html>`;
}

export function buildInvoiceHtml(data: InvoicePrintData, format: InvoiceFormat): string {
  if (format === "a4") return buildA4InvoiceHtml(data);
  return buildPosTicketHtml(data, format === "pos58" ? 58 : 80);
}

export function printInvoice(data: InvoicePrintData, format: InvoiceFormat, job: PrintJobOptions = {}): void {
  const isA4 = format === "a4";
  printFullHtml(buildInvoiceHtml(data, format), {
    kind: isA4 ? "a4" : "ticket",
    widthMm: isA4 ? undefined : format === "pos58" ? 58 : 80,
    ...job,
  });
}